import React from 'react';
import { AlertCircle, ChevronRight } from 'lucide-react';
import type { RetreatEvent } from '../../types/events';
import { dateMarker, isUpcoming, sortByDate } from '../../utils/eventDates';
interface EventListProps {
  events: RetreatEvent[];
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRetry: () => void;
}
function EventListItem({
  event,
  selected,
  onSelect
}: {
  event: RetreatEvent;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const marker = dateMarker(event.eventDate);
  return <li data-id="element-150" data-mp="3qiwtl9hs7lk1">
      <button type="button" onClick={() => onSelect(event.id)} aria-current={selected ? 'true' : undefined} className={`group flex w-full items-center gap-3 rounded-sm border px-4 py-3.5 text-left transition-colors ${selected ? 'border-pine bg-cream' : 'border-pine/15 bg-creamDeep/60 hover:border-pine/35 hover:bg-cream'}`} data-id="element-151" data-mp="3qidjgeydi1gk">
        <div className="min-w-0 flex-1" data-id="element-152" data-mp="1jxbz9fqce57l">
          <div className="mb-1 flex items-center justify-between gap-3" data-id="element-153" data-mp="1ju7lbqn2rv3q">
            <span className="text-xs font-medium tracking-[0.12em] text-clay/70" data-id="element-154" data-mp="1ou2b1qv46en7">
              {marker ? `${marker.weekday} ${marker.day} ${marker.month}` : 'No date'}
            </span>
            {event.published ? <span className="rounded-full bg-pine px-2 py-0.5 text-[10px] font-medium tracking-[0.12em] text-cream" data-id="element-155" data-mp="1ezagyx0qq9x6">
                LIVE
              </span> : <span className="rounded-full border border-pine/30 px-2 py-0.5 text-[10px] font-medium tracking-[0.12em] text-clay" data-id="element-156" data-mp="15y2j2g24zb3m">
                DRAFT
              </span>}
          </div>
          <p className="line-clamp-2 font-serif text-lg leading-snug text-pine" data-id="element-157" data-mp="w77sx0lrrrcw">{event.title}</p>
        </div>
        <ChevronRight className="h-4 w-4 flex-none text-clay/40 lg:hidden" aria-hidden="true" data-id="element-158" data-mp="i7qb556w5ed1" />
      </button>
    </li>;
}
export function EventList({
  events,
  status,
  error,
  selectedId,
  onSelect,
  onRetry
}: EventListProps) {
  if (status === 'loading') {
    return <div className="space-y-2" aria-busy="true" aria-label="Loading events" data-id="element-159" data-mp="2k2w98jmgg4tg">
        {[0, 1, 2].map(item => <div key={item} className="h-[76px] animate-pulse rounded-sm bg-creamDeep" data-id="element-160" data-mp="2f3b945vjtqf5" />)}
      </div>;
  }
  if (status === 'error') {
    return <div className="rounded-sm border border-ember/30 bg-cream p-5" data-id="element-161" data-mp="37t09qi4cjog8">
        <div className="mb-3 flex gap-2.5 text-sm text-clay" data-id="element-162" data-mp="32v8a11xgmy1h">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-ember" aria-hidden="true" data-id="element-163" data-mp="32svsgpvm6poy" />
          <p data-id="element-164" data-mp="2xu2ekwcf0wo9">Events couldn’t be loaded. {error}</p>
        </div>
        <button type="button" onClick={onRetry} className="text-sm font-medium text-pine underline decoration-pine/30 underline-offset-4 hover:text-ember" data-id="element-165" data-mp="2tv217tcpim8n">
          Try again
        </button>
      </div>;
  }
  if (events.length === 0) {
    return <div className="rounded-sm border border-dashed border-pine/25 p-6" data-id="element-166" data-mp="3u39sqjpc5n30">
        <p className="font-serif text-xl text-pine" data-id="element-167" data-mp="1yekpewu0iec">No events yet.</p>
        <p className="mt-2 text-sm font-light leading-relaxed text-clay" data-id="element-168" data-mp="1wld9xee9u5a">
          Add the next one and it’ll appear on the site.
        </p>
      </div>;
  }
  const upcoming = events.filter(event => isUpcoming(event.eventDate));
  const past = sortByDate(events.filter(event => !isUpcoming(event.eventDate)), 'desc');
  return <div className="space-y-8" data-id="element-169" data-mp="llvr15uky50r">
      <div data-id="element-170" data-mp="ll3y62sdale6">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-clay/70" data-id="element-171" data-mp="lorqwzpzn3ru">
          Upcoming
        </h2>
        {upcoming.length > 0 ? <ul className="space-y-2" data-id="element-172" data-mp="umaf2cyxhy7y">
            {upcoming.map(event => <EventListItem key={event.id} event={event} selected={event.id === selectedId} onSelect={onSelect} data-id="element-173" data-mp="2rg57j1no78r9" />)}
          </ul> : <p className="text-sm font-light text-clay" data-id="element-174" data-mp="2138icm8ju6hf">
            Nothing upcoming — the events section is hidden on the site until you add one.
          </p>}
      </div>

      {past.length > 0 && <details className="group" data-id="element-175" data-mp="2b27lxqegjbxk">
          <summary className="cursor-pointer list-none text-xs font-medium uppercase tracking-[0.2em] text-clay/70 transition-colors hover:text-pine" data-id="element-176" data-mp="263msc9tpnqi3">
            Past events ({past.length})
          </summary>
          <ul className="mt-3 space-y-2 opacity-80" data-id="element-177" data-mp="1df7dpoehra7a">
            {past.map(event => <EventListItem key={event.id} event={event} selected={event.id === selectedId} onSelect={onSelect} data-id="element-178" data-mp="1s3lhq2nn94pf" />)}
          </ul>
        </details>}
    </div>;
}